import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowLeft, XCircle } from 'lucide-react';
import Navigation from '../layout/Navigation';
import FloatingButtons from './FloatingButtons';
import Footer from '../sections/Footer';
import { useLanguage } from '../../i18n/LanguageContext';

const PageLayout = ({ children }) => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#030810] text-white">
      <Navigation />
      <FloatingButtons />
      <main>
        {children}
      </main>
      <Footer />
    </div>
  );
};

// Hero component for SEO pages
export const PageHero = ({ 
  image, 
  title, 
  subtitle, 
  description,
  showBackButton = true 
}) => {
  const { t } = useLanguage();
  
  const handleBackClick = (e) => {
    e.preventDefault();
    window.location.href = '/';
  };

  return (
    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030810] via-[#030810]/70 to-[#030810]/30" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-24">
        {showBackButton && (
          <a 
            href="/"
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            {t('seoCommon.backToHome')}
          </a>
        )}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
            {title}
          </span>
        </h1>
        {subtitle && (
          <p className="text-xl md:text-2xl text-white/90 font-light mb-4">
            {subtitle}
          </p>
        )}
        {description && (
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {description}
          </p>
        )}
      </div>
    </section>
  );
};

// Section component for content
export const PageSection = ({ title, children, className = '' }) => {
  return (
    <section className={`py-16 md:py-24 ${className}`}>
      <div className="max-w-6xl mx-auto px-6">
        {title && (
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            {title}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
};

// CTA Section - Fixed links to go to home page sections
export const PageCTA = ({ 
  title = "¿Listo para crear tu experiencia?",
  subtitle = "Contáctanos y diseñemos juntos el show perfecto para tu evento",
  whatsappNumber = "525578535742",
  whatsappMessage = "Hola, me interesa conocer más sobre sus servicios"
}) => {
  const { t } = useLanguage();
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
  
  const handleQuoteClick = (e) => {
    e.preventDefault();
    window.location.href = '/#contacto';
  };
  
  return (
    <section className="py-20 bg-gradient-to-b from-[#030810] to-[#0a1628]">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
          {title}
        </h2>
        <p className="text-gray-400 text-lg mb-8">
          {subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full transition-all hover:shadow-[0_0_30px_rgba(34,197,94,0.4)]"
          >
            <MessageCircle size={24} />
            {t('seoCommon.contactWhatsApp')}
          </a>
          <a
            href="/#contacto"
            onClick={handleQuoteClick}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-full transition-all hover:shadow-[0_0_30px_rgba(0,240,255,0.4)]"
          >
            {t('seoCommon.requestQuote')}
          </a>
        </div>
      </div>
    </section>
  );
};

// Gallery component with lightbox
export const PageGallery = ({ images, title }) => {
  const [lightboxImage, setLightboxImage] = useState(null);

  return (
    <div className="mt-12">
      {title && (
        <h3 className="text-2xl font-bold text-white mb-6">{title}</h3>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {images.map((image, index) => (
          <div 
            key={index}
            className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all cursor-pointer"
            onClick={() => setLightboxImage(image)}
          >
            <img 
              src={image.url} 
              alt={image.title || `Imagen ${index + 1}`}
              loading="lazy"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <XCircle size={40} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.url} 
              alt={lightboxImage.title || 'Imagen'}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            {lightboxImage.title && (
              <div className="mt-4 text-center">
                <h3 className="text-white text-xl font-bold">{lightboxImage.title}</h3>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageLayout;
